import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@/components/Collapse';
import { businessSeatLimit } from '@/features/billing/lib/seatPacks';
import type { Organization } from '@/types/gql.generated';
import { Box, Button, Flex, Text } from '@/ui';
import { SeatAdjustPanel } from './SeatAdjustPanel';

type Props = {
  organization: Organization;
  /** Accepted members + live pending invites — the server's occupancy count. */
  occupiedSeats: number;
};

// Seat capacity for a business org: a quiet summary by default, expanding into
// the adjustment controls on demand. Adjusting seats is a rare, deliberate act,
// so it shouldn't compete with the roster for attention.
export const SeatsSection = ({ organization, occupiedSeats }: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations.seats',
  });
  const [isOpen, setIsOpen] = useState(false);

  const licensedSeats =
    organization.seatLimit ?? businessSeatLimit(organization.seatPacks ?? 0);

  return (
    <Flex direction="column" gap="3">
      <Flex
        align="center"
        borderColor="gray.200"
        borderRadius="ah-xl"
        borderWidth="1px"
        gap="4"
        justify="space-between"
        px="5"
        py="4"
      >
        <Box>
          <Text fontWeight="medium">{t('current_heading')}</Text>
          <Text color="customgray.mid" fontSize="sm">
            {t('current_summary', {
              licensed: licensedSeats,
              inUse: occupiedSeats,
            })}
          </Text>
        </Box>
        <Button
          flexShrink="0"
          variant="secondary"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? t('close') : t('adjust_button')}
        </Button>
      </Flex>

      <Collapse in={isOpen}>
        <SeatAdjustPanel
          occupiedSeats={occupiedSeats}
          organization={organization}
          onDone={() => setIsOpen(false)}
        />
      </Collapse>
    </Flex>
  );
};
